import type { ScreenerCVDCandle } from '@/types'
import type { ScalePoint } from './chartScale'
import { findLastKnownIndex } from './chartUtils'

type PlottedCVDCandle = ScreenerCVDCandle & {
  open: number; close: number
}

export function buildCVDPlot(candles: readonly ScreenerCVDCandle[], metric: 'coins' | 'quote' = 'coins') {
  const points: PlottedCVDCandle[] = []
  const series: [number, number | null][] = []
  const candleSeries: [number, number | null, number | null, number | null, number | null][] = []
  const unknownWickSeries: [number, number][] = []
  const lineScale: ScalePoint[] = []
  const candleScale: ScalePoint[] = []
  const ordered = [...candles].filter((candle) => Number.isFinite(candle.timestamp))
    .sort((a, b) => a.timestamp - b.timestamp)
  let previous: PlottedCVDCandle | null = null
  for (const source of ordered) {
    const candle = metric === 'coins' ? { ...source } : {
      ...source, open: source.quoteOpen ?? null, high: source.quoteHigh ?? null,
      low: source.quoteLow ?? null, close: source.quoteClose ?? null, delta: source.quoteDelta ?? null,
    }
    const timestamp = candle.timestamp * 1000
    if (candle.open == null || candle.close == null || !Number.isFinite(candle.open) || !Number.isFinite(candle.close)) {
      // Missing coverage stays a real gap. Only the backend may produce a flat
      // quiet bucket after verifying durable progress and transport coverage.
      series.push([timestamp, null])
      candleSeries.push([timestamp, null, null, null, null])
      lineScale.push([timestamp, null])
      candleScale.push([timestamp, null])
      previous = null
      continue
    }
    const wickKnown = candle.wickQuality !== 'unknown'
      && candle.low != null && candle.high != null && Number.isFinite(candle.low) && Number.isFinite(candle.high)
      && candle.low <= Math.min(candle.open, candle.close) && candle.high >= Math.max(candle.open, candle.close)
    const point: PlottedCVDCandle = { ...candle, open: candle.open, close: candle.close,
      high: wickKnown ? candle.high : null, low: wickKnown ? candle.low : null,
      wickQuality: wickKnown ? candle.wickQuality ?? 'sampled' : 'unknown' }
    points.push(point)
    series.push([timestamp, point.close])
    candleSeries.push(wickKnown ? [timestamp, point.open, point.close, point.low, point.high]
      : [timestamp, null, null, null, null])
    if (!wickKnown) unknownWickSeries.push([timestamp, point.close])
    if (previous) lineScale.push([timestamp, previous.close])
    lineScale.push([timestamp, point.close])
    candleScale.push([timestamp, point.low ?? Math.min(point.open, point.close)],
      [timestamp, point.high ?? Math.max(point.open, point.close)])
    previous = point
  }
  const timestamps = points.map((candle) => candle.timestamp * 1000)
  return { points, timestamps, series, candleSeries, unknownWickSeries,
    scaleSeries: [lineScale], candleScaleSeries: [candleScale], latest: points[points.length - 1] ?? null }
}

export function cvdCandleAtTime(
  plot: Pick<ReturnType<typeof buildCVDPlot>, 'points' | 'timestamps'>,
  timestampMs: number,
  intervalSeconds: number,
): PlottedCVDCandle | null {
  if (!Number.isFinite(timestampMs) || !(intervalSeconds > 0)) return null
  const index = findLastKnownIndex(plot.timestamps, timestampMs)
  const point = index < 0 ? null : plot.points[index]
  return point && timestampMs < (point.timestamp + intervalSeconds) * 1000 ? point : null
}
